import { useQuery } from '@tanstack/react-query'
import { resolveLoyaltyDomain } from '~/lib/loyalty-domain'
import { getLoyaltyPropertyCode, useLoyaltyProperties } from '~/hooks/use-loyalty-properties'
import { formatPhoneNumber } from '~/lib/common-utils'

const LOYALTY_API_URL = process.env.NEXT_PUBLIC_LOYALTY_API_URL

export interface LoyaltyNoticeResponse {
  state: 'ESTIMATED' | 'POSTED' | 'PROCESSING' | 'NOT_MEMBER'
  member_match_status: 'MATCHED' | 'NOT_FOUND'
  points: number
  message: string
  cta: { target: 'LOGIN' | 'REGISTER' | 'POINTS_OVERVIEW'; [key: string]: any }
}

interface UseLoyaltyNoticeParams {
  memberPhone?: string | null
  hotelId?: string | number | null
  amountVnd?: number | null
  viewerAuthenticated: boolean
  enabled: boolean
}

async function fetchLoyaltyNotice(body: {
  domain: string
  member_phone: string
  property_code: string
  amount_vnd: number
  viewer_authenticated: boolean
}): Promise<LoyaltyNoticeResponse | null> {
  // POST /public/loyalty-notice (LOYAL-16) — public, unauthenticated read-only
  // endpoint (security: [], no JWT/API key). Called directly, no Next.js
  // proxy: POST is only to keep member_phone out of the URL/access log, not
  // to hide a secret, and this endpoint has none.
  const res = await fetch(`${LOYALTY_API_URL}/public/loyalty-notice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domain: body.domain,
      member_phone: formatPhoneNumber(body.member_phone),
      phase: 'BEFORE_CHECKOUT',
      amount_vnd: body.amount_vnd,
      property_code: body.property_code,
      service_type: 'ROOM',
      channel: 'DIRECT',
      viewer_authenticated: body.viewer_authenticated,
    }),
  })
  if (!res.ok) return null
  return res.json()
}

// Loyalty reward notice block shown on the step-3 success popup (LOYAL-16).
// Resolves silently to `null` (never throws into the UI) whenever a
// prerequisite is missing — no phone, no property_code match for this
// hotel yet (see hooks/use-loyalty-properties.ts), no resolvable tenant
// domain, or the endpoint isn't configured — so the reward box just doesn't
// render rather than showing wrong/placeholder data.
export function useLoyaltyNotice({
  memberPhone,
  hotelId,
  amountVnd,
  viewerAuthenticated,
  enabled,
}: UseLoyaltyNoticeParams) {
  const { data: properties } = useLoyaltyProperties()
  const propertyCode = getLoyaltyPropertyCode(properties, hotelId)
  const canFetch = enabled && !!LOYALTY_API_URL && !!memberPhone && !!propertyCode && !!amountVnd

  return useQuery<LoyaltyNoticeResponse | null>({
    queryKey: [
      'loyalty-notice',
      memberPhone,
      propertyCode,
      amountVnd,
      process.env.NEXT_PUBLIC_DOMAIN_URL,
    ],
    queryFn: () =>
      fetchLoyaltyNotice({
        domain: process.env.NEXT_PUBLIC_DOMAIN_URL as string,
        member_phone: memberPhone as string,
        property_code: propertyCode as string,
        amount_vnd: amountVnd as number,
        viewer_authenticated: viewerAuthenticated,
      }),
    enabled: canFetch,
    staleTime: 60 * 1000,
    retry: false,
  })
}
